import _ from 'lodash';
import { getDiscountPercentage } from '../../../../common/global/helpers';
import { config } from '../../config';
import { onlineFilter } from '../../constants';
import { BuyerTypes, SortingOrderTypes } from '../../enums';
import { Category } from '../../models/Categories';
import {
  BrandFilterType,
  BuyerTypeFilter,
  CountyFilter,
  TaxFilter,
} from '../../models/Filters';
import { PriceRange } from '../../models/PriceRange';
import { ProductCardModel, ProductListModel } from '../../models/Product';
import { queryParamKeys } from './constants';
import { filterSelectionDefaults, FilterSelectionsState } from './types';

function filterByBrand(
  products: ProductListModel[],
  selectedBrandIds?: number[],
): ProductListModel[] {
  if (!selectedBrandIds?.length) return products;
  return products.filter(p => selectedBrandIds.includes(p.brandId));
}

function filterByBuyerType(
  products: ProductListModel[],
  selectedBuyerTypes: BuyerTypes[],
): ProductListModel[] {
  if (!selectedBuyerTypes.length) return products;
  return products.filter(
    p => p.buyerType && selectedBuyerTypes.includes(p.buyerType),
  );
}

function filterByTaxClass(
  products: ProductListModel[],
  selectedIds: number[],
  getProductTaxClassId: (p: ProductListModel) => number,
): ProductListModel[] {
  if (!selectedIds?.length) return products;
  return products.filter(p => {
    const taxId = getProductTaxClassId(p);
    return taxId !== undefined && selectedIds.includes(taxId);
  });
}

function filterByCounty(
  products: ProductListModel[],
  selectedCountyIds: number[],
): ProductListModel[] {
  if (!selectedCountyIds.length) return products;

  const allowed = new Set([...selectedCountyIds, onlineFilter]);

  return products.filter(p => p.countyFilterIds.some(id => allowed.has(id)));
}

function isDiscountedProduct(p: ProductListModel): boolean {
  return (
    p.price.discountedPrice !== null &&
    p.price.discountedPrice !== p.price.defaultPrice
  );
}

function hasAnyCategory(
  p: ProductListModel,
  selectedCategories: Set<number>,
): boolean {
  return p.path.some(id => selectedCategories.has(id));
}

export const getFiltredProducts = (
  products?: ProductListModel[],
  selections?: FilterSelectionsState,
): ProductListModel[] | undefined => {
  if (!products) return undefined;
  if (!selections) return undefined;
  let result = [...products];

  // Brand filter
  result = filterByBrand(result, selections.selectedBrandIds);

  // Buyer type filter
  result = filterByBuyerType(result, selections.selectedBuyerTypes);

  // Employee tax class filter
  result = filterByTaxClass(
    result,
    selections.selectedTaxClassIds,
    getProductTaxClassId,
  );

  // Employer tax class filter
  result = filterByTaxClass(
    result,
    selections.selectedEmployerTaxClassIds,
    getProductEmployerTaxClassId,
  );

  // County + online filter
  result = filterByCounty(result, selections.selectedCountyIds);

  // Discounted only filter
  if (selections.showDiscountedProducts) {
    result = result.filter(isDiscountedProduct);
  }

  if (
    selections.selectedCategories &&
    selections.selectedCategories?.size > 0
  ) {
    result = result.filter(p =>
      hasAnyCategory(p, selections.selectedCategories!),
    );
  }

  // Price range filter
  if (products && selections.priceRange) {
    result = filterProductsByRange(result, selections.priceRange);
  }

  return result;
};

export const filterProductsByRange = (
  products: ProductListModel[],
  priceRange: PriceRange,
): ProductListModel[] => {
  const start = priceRange.min;
  const end = priceRange.max;

  return _.filter(products, (p: ProductListModel) => {
    return (
      start <= (p.price.discountedPrice ?? p.price.defaultPrice) &&
      (p.price.discountedPrice ?? p.price.defaultPrice) <= end
    );
  });
};

export const getSortedProduts: {
  [sortId: number]: (
    products: ProductListModel[] | undefined,
  ) => ProductListModel[];
} = {
  [SortingOrderTypes.TheMostRelevant]: products => {
    return _.orderBy(products, ['searchScore', 'views'], ['desc']);
  },
  [SortingOrderTypes.TheMostPopular]: products => {
    const result = [...(products ?? [])];
    result.sort(productCardsDefaultSorting);
    return result;
  },
  [SortingOrderTypes.TheLowestPrice]: products => {
    return _.orderBy(
      products,
      ['price.defaultPrice', 'price.discountedPrice'],
      ['asc'],
    );
  },
  [SortingOrderTypes.TheHighestPrice]: products => {
    return _.orderBy(
      products,
      ['price.defaultPrice', 'price.discountedPrice'],
      ['desc'],
    );
  },
  [SortingOrderTypes.BiggestDiscount]: products => {
    return _.orderBy(
      products,
      p => {
        return getDiscountPercentage(
          p.price.defaultPrice,
          p.price.discountedPrice,
        );
      },
      ['desc'],
    );
  },
  [SortingOrderTypes.LastAdded]: products => {
    return _.orderBy(products, ['dateAdded'], ['desc']);
  },
};

export const getSortedProductCards: {
  [sortId: number]: (
    products: ProductCardModel[] | undefined,
  ) => ProductCardModel[];
} = {
  [SortingOrderTypes.TheMostRelevant]: products => {
    return _.orderBy(products, ['searchScore', 'views'], ['desc']);
  },
  [SortingOrderTypes.TheMostPopular]: products => {
    const result = [...(products ?? [])];
    result.sort(productCardsDefaultSorting);
    return result;
  },
  [SortingOrderTypes.TheLowestPrice]: products => {
    if (products?.[0]?.brandId) {
      return _.orderBy(products, ['price.priceRanges[0]'], ['asc']);
    }
    return _.orderBy(
      products,
      ['price.defaultPrice', 'price.discountedPrice'],
      ['asc'],
    );
  },
  [SortingOrderTypes.TheHighestPrice]: products => {
    if (products?.[0]?.brandId) {
      return _.orderBy(products, ['price.priceRanges[1]'], ['desc']);
    }

    return _.orderBy(
      products,
      ['price.defaultPrice', 'price.discountedPrice'],
      ['desc'],
    );
  },
  [SortingOrderTypes.BiggestDiscount]: products => {
    return _.orderBy(
      products,
      p => {
        return getDiscountPercentage(
          p.price.defaultPrice,
          p.price.discountedPrice,
        );
      },
      ['desc'],
    );
  },
  [SortingOrderTypes.LastAdded]: products => {
    return _.orderBy(products, ['dateAdded'], ['desc']);
  },
};

const productCardsDefaultSorting = (
  a: ProductCardModel | ProductListModel,
  b: ProductCardModel | ProductListModel,
): number => {
  // 1. Products with manufacturerSortOrder = 0 are pushed to the end of the list
  if (a.brandSortOrder === 0 || b.brandSortOrder === 0) {
    if (a.brandSortOrder === 0 && b.brandSortOrder !== 0) return 1;
    if (b.brandSortOrder === 0 && a.brandSortOrder !== 0) return -1;
  }

  // 2. Primary sorting: by manufacturerSortOrder in ascending order
  if (a.brandSortOrder !== b.brandSortOrder) {
    return a.brandSortOrder - b.brandSortOrder;
  }

  // 3. Tie-breaker: sort by average views in descending order
  return b.views - a.views;
};

//#region update filters after selection

export const getUpdatedFilters = (
  products?: ProductListModel[],
  filteredProducts?: ProductListModel[],
  counties?: CountyFilter[],
  brands?: BrandFilterType[],
  tax?: TaxFilter[],
  employerTax?: TaxFilter[],
  buyerTypes?: BuyerTypeFilter[],
  selectedCategories?: Set<number>,
  isPriceChange?: boolean,
  hasFiltersSelected?: boolean,
):
  | {
      filteredCounties: CountyFilter[];
      filteredBrands: BrandFilterType[];
      filteredTax: TaxFilter[];
      filteredEmployerTax: TaxFilter[];
      discountedProductsCount: number;
      priceRange: PriceRange;
      filteredBuyerTypes: BuyerTypeFilter[];
      filteredSelectedCategories: Set<number>;
    }
  | undefined => {
  if (!filteredProducts || !products) return undefined;

  let filteredCounties = [] as CountyFilter[];
  let filteredBrands = [] as BrandFilterType[];
  let filteredTax = [] as TaxFilter[];
  let filteredEmployerTax = [] as TaxFilter[];
  let filteredBuyerTypes = [] as BuyerTypeFilter[];
  let filteredSelectedCategories = new Set<number>();

  if (counties) {
    filteredCounties = getAvailableInList(filteredProducts, counties);
  }
  if (brands) {
    filteredBrands = getBrandList(filteredProducts, brands);
  }
  if (tax) {
    filteredTax = getTaxList(filteredProducts, tax);
  }
  if (employerTax) {
    filteredEmployerTax = getEmployerTaxList(filteredProducts, employerTax);
  }
  if (buyerTypes) {
    filteredBuyerTypes = getBuyerTypeList(filteredProducts, buyerTypes);
  }
  if (selectedCategories && selectedCategories.size > 0) {
    filteredSelectedCategories = getSelectedCategories(
      filteredProducts,
      selectedCategories,
    );
  }

  const discountedProductsCount = getDiscountedProductsCount(filteredProducts);
  const priceRange = getPriceRange(
    !hasFiltersSelected && !isPriceChange ? products : filteredProducts,
  );

  return {
    filteredCounties,
    filteredBrands,
    filteredTax,
    filteredEmployerTax,
    discountedProductsCount,
    priceRange,
    filteredBuyerTypes,
    filteredSelectedCategories,
  };
};

export const getAvailableInList = (
  products: ProductListModel[],
  filterList: CountyFilter[],
): CountyFilter[] => {
  const countyIdsFromProducts = _.uniq(
    products.flatMap(p => p.countyFilterIds),
  );
  return filterList.filter(f => countyIdsFromProducts.includes(f.filterId));
};

export const getBrandList = (
  products: ProductListModel[],
  filterList: BrandFilterType[],
): BrandFilterType[] => {
  const brandIdsFromProducts = _.uniq(products.map(p => p.brandId));
  return filterList.filter(f => brandIdsFromProducts.includes(f.brandId));
};

const getBuyerTypeList = (
  products: ProductListModel[],
  filterList: BuyerTypeFilter[],
): BuyerTypeFilter[] => {
  const buyerTypesFromProducts = _.uniq(products.map(p => p.buyerType));
  return filterList.filter(f => buyerTypesFromProducts.includes(f.buyerType));
};

export const getTaxList = (
  products: ProductListModel[],
  filterList: TaxFilter[],
): TaxFilter[] => {
  const taxClassIdsFromProducts = _.uniq(
    products.map(p => getProductTaxClassId(p)),
  );
  return filterList.filter(f => taxClassIdsFromProducts.includes(f.taxClassId));
};

export const getEmployerTaxList = (
  products: ProductListModel[],
  filterList: TaxFilter[],
): TaxFilter[] => {
  const taxClassIdsFromProducts = _.uniq(
    products.map(p => getProductEmployerTaxClassId(p)),
  );
  return filterList.filter(f => taxClassIdsFromProducts.includes(f.taxClassId));
};

const getProductTaxClassId = (product: ProductListModel): number => {
  return product.tax.employeeTaxClassId === 0
    ? config.zeroProcentTaxtClassId
    : product.tax.employeeTaxClassId;
};

const getProductEmployerTaxClassId = (product: ProductListModel): number => {
  return product.tax.employerTaxClassId === 0
    ? config.zeroProcentTaxtClassId
    : product.tax.employerTaxClassId;
};

export const getSelectedCategories = (
  products: ProductListModel[],
  selectedCategories: Set<number>,
): Set<number> => {
  const result = new Set<number>();
  const flatCategoriesFromProducts = new Set(
    _.uniq(products.map(p => p.path)).flat(),
  );

  selectedCategories.forEach(categ => {
    if (flatCategoriesFromProducts.has(categ)) {
      result.add(categ);
    }
  });

  return result;
};

export const getPriceRange = (products: ProductListModel[]): PriceRange => {
  const minPrice = _.min(
    products.map(p => p.price.discountedPrice ?? p.price.defaultPrice),
  );
  const maxPrice = _.max(
    products.map(p => p.price.discountedPrice ?? p.price.defaultPrice),
  );
  return {
    min: minPrice ?? 0,
    max: maxPrice ?? 100,
  };
};

export const getDiscountedProductsCount = (
  products: ProductListModel[],
): number => {
  return products.filter(
    p =>
      p.price.discountedPrice !== null &&
      p.price.discountedPrice !== p.price.defaultPrice,
  ).length;
};

export const getCurentCategory = (
  rootCategory: Category,
  categoryId: string | undefined,
): Category | undefined => {
  if (rootCategory && categoryId) {
    for (const category of rootCategory.children) {
      if (category.id === Number(categoryId)) {
        return category;
      }

      if (category.children && category.children.length > 0) {
        const result = getCurentCategory(category, categoryId);

        if (result) return result;
      }
    }
  }

  return undefined;
};

export const hasSelectedFilters = (
  selections: FilterSelectionsState,
): boolean => {
  return (
    selections.selectedBrandIds.length > 0 ||
    selections.selectedBuyerTypes.length > 0 ||
    (selections.selectedCategories &&
      selections.selectedCategories?.size > 0) ||
    selections.selectedCountyIds.length > 0 ||
    selections.selectedTaxClassIds.length > 0 ||
    selections.showDiscountedProducts
  );
};

//#endregion

export const getFiltredSelectionState = (
  searchParams: URLSearchParams,
): FilterSelectionsState => {
  const brandIds: number[] = searchParams
    .getAll(queryParamKeys.brandIdFilter)
    .map(Number);

  const countyIds: number[] = searchParams
    .getAll(queryParamKeys.countyIdFilter)
    .map(Number);

  const buyerTypeIds: BuyerTypes[] = searchParams
    .getAll(queryParamKeys.buyerTypeIdFilter)
    .map(Number);

  const taxClassIds: number[] = searchParams
    .getAll(queryParamKeys.taxClassIdFilter)
    .map(Number);

  const showDiscounted =
    searchParams.get(queryParamKeys.showDiscountedFilter) === 'true';

  const priceRangeMin = Number(
    searchParams.get(queryParamKeys.priceRangeFilterMin),
  );

  const priceRangeMax = Number(
    searchParams.get(queryParamKeys.priceRangeFilterMax),
  );

  let priceRange: PriceRange | undefined;

  if (priceRangeMin && priceRangeMax) {
    priceRange = {
      min: priceRangeMin,
      max: priceRangeMax,
    };
  }

  return {
    ...filterSelectionDefaults,
    selectedBrandIds: brandIds,
    selectedCountyIds: countyIds,
    selectedBuyerTypes: buyerTypeIds,
    selectedTaxClassIds: taxClassIds,
    showDiscountedProducts: showDiscounted,
    priceRange: priceRange,
  };
};

export const clearFilterQueryParams = () => {
  const params = new URLSearchParams(location.search);

  params.delete(queryParamKeys.brandIdFilter);
  params.delete(queryParamKeys.buyerTypeIdFilter);
  params.delete(queryParamKeys.countyIdFilter);
  params.delete(queryParamKeys.taxClassIdFilter);
  params.delete(queryParamKeys.showDiscountedFilter);
  params.delete(queryParamKeys.priceRangeFilterMin);
  params.delete(queryParamKeys.priceRangeFilterMax);

  updateUrlParams(params);
};

export const updateArrayQueryParam = (name: string, values: number[]) => {
  const params = new URLSearchParams(location.search);

  params.delete(name);
  values.forEach(val => {
    params.append(name, val.toString());
  });

  updateUrlParams(params);
};

export const updateBooleanQueryParam = (name: string, value: boolean) => {
  const params = new URLSearchParams(location.search);

  if (value) {
    params.set(name, value.toString());
  } else {
    params.delete(name);
  }

  updateUrlParams(params);
};

export const updatePriceRangeQueryParam = (range: PriceRange) => {
  const params = new URLSearchParams(location.search);

  params.set(queryParamKeys.priceRangeFilterMin, range.min.toString());
  params.set(queryParamKeys.priceRangeFilterMax, range.max.toString());

  updateUrlParams(params);
};

export const updateSortQueryParam = (sortId: SortingOrderTypes) => {
  const params = new URLSearchParams(location.search);

  params.set(queryParamKeys.sortId, sortId.toString());

  updateUrlParams(params);
};

export const updatePagingQueryParam = (skip: number, take: number) => {
  const params = new URLSearchParams(location.search);

  params.set(queryParamKeys.skip, skip.toString());
  params.set(queryParamKeys.take, take.toString());

  updateUrlParams(params);
};

export const updateUrlParams = (params: URLSearchParams) => {
  const paramsString = params.toString();

  const newUrl = `${location.pathname}${
    paramsString ? '?' : ''
  }${paramsString}`;

  history.replaceState({}, '', newUrl);
};
